import { getComputedColor } from "../../../../utils/AppColors";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { BannerIn } from "../types";

export type BannerTheme = "brand" | "light" | "dark";

export type BannerThemePreset = {
  theme: BannerTheme;
  label: string;
  backgroundColors: string[];
  borderColors: string[];
  titleColor: string;
  shortDescriptionColor: string;
  longDescriptionColor: string;
  detailsColor: string;
  timerBackgroundColor: string;
  timerTextColor: string;
  timerIconColor: string;
  timerBorderColor: string;
  timerBorderWidth: number;
  timerShadow: boolean;
  claimTextColor: string;
  claimBackgroundColors: string[];
  claimBorderColor: string;
};

/**
 * Converts an `rgb()/rgba()` palette value into an uppercase hex string so it
 * satisfies the banner form's `#RRGGBB`/`#RRGGBBAA` colour fields. Values that
 * are already hex (or unparseable) are returned uppercased / untouched.
 */
const rgbaToHex = (value: string): string => {
  const match = /rgba?\(([^)]+)\)/i.exec(value);
  if (!match) {
    return value.startsWith("#") ? value.toUpperCase() : value;
  }

  const parts = match[1].split(",").map((part) => part.trim());
  const [r, g, b] = parts.map(Number);
  const alpha = parts[3] === undefined ? 1 : Number(parts[3]);
  if ([r, g, b, alpha].some((channel) => Number.isNaN(channel))) {
    return value;
  }

  const toHex = (channel: number) =>
    Math.round(channel).toString(16).padStart(2, "0");
  const alphaHex = alpha < 1 ? toHex(alpha * 255) : "";

  return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`.toUpperCase();
};

const withOpacity = (color: string, opacity: number): string => {
  const match = /^#([0-9A-F]{6})(?:[0-9A-F]{2})?$/i.exec(color);
  if (!match) return color;
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();
  return `#${match[1].toUpperCase()}${alpha}`;
};

const resolveThemeColor = (
  cssVariable: string,
  opacity?: number,
): string => {
  const live = getComputedColor(cssVariable);
  if (!live) return "";
  const color = rgbaToHex(live);
  return opacity === undefined ? color : withOpacity(color, opacity);
};

const resolveThemeColors = (...cssVariables: string[]): string[] =>
  cssVariables.map((variable) => resolveThemeColor(variable)).filter(Boolean);

type ThemeColorSource = {
  cssVariable: string;
  opacity?: number;
};

type BannerThemeConfig = {
  theme: BannerTheme;
  label: string;
  backgroundColors: string[];
  borderColors: string[];
  titleColor: ThemeColorSource;
  shortDescriptionColor: ThemeColorSource;
  longDescriptionColor: ThemeColorSource;
  detailsColor: ThemeColorSource;
  timerBackgroundColor: ThemeColorSource;
  timerTextColor: ThemeColorSource;
  timerIconColor: ThemeColorSource;
  timerBorderColor: ThemeColorSource;
  claimTextColor: ThemeColorSource;
  claimBackgroundColors: string[];
  claimBorderColor: ThemeColorSource;
};

const getBannerThemeConfigs = (): BannerThemeConfig[] => [
  {
    theme: "brand",
    label: AppStrings.PAM_DYNAMIC_BONUS_BANNER_THEME_BRAND,
    backgroundColors: [
      "--COLOR-PRIMARY_BUTTON_0",
      "--COLOR-PRIMARY_BUTTON_1",
    ],
    borderColors: ["--COLOR-PRIMARY"],
    titleColor: { cssVariable: "--COLOR-PRIMARY_BACKGROUND_COLOR" },
    shortDescriptionColor: { cssVariable: "--COLOR-BETCARD_HEADER_BG" },
    longDescriptionColor: { cssVariable: "--COLOR-BETCARD_HEADER_BG" },
    detailsColor: { cssVariable: "--COLOR-PRIMARY_BACKGROUND_COLOR" },
    timerBackgroundColor: {
      cssVariable: "--COLOR-LIGHT_TEXT_COLOR",
      opacity: 0.5,
    },
    timerTextColor: { cssVariable: "--COLOR-PRIMARY_BACKGROUND_COLOR" },
    timerIconColor: { cssVariable: "--COLOR-PRIMARY_BACKGROUND_COLOR" },
    timerBorderColor: { cssVariable: "--COLOR-PRIMARY_BACKGROUND_COLOR" },
    claimTextColor: { cssVariable: "--COLOR-PRIMARY_BACKGROUND_COLOR" },
    claimBackgroundColors: [
      "--COLOR-PRIMARY_BUTTON_0",
      "--COLOR-PRIMARY_BUTTON_1",
    ],
    claimBorderColor: { cssVariable: "--COLOR-PRIMARY" },
  },
  {
    theme: "light",
    label: AppStrings.PAM_DYNAMIC_BONUS_BANNER_THEME_LIGHT,
    backgroundColors: [
      "--COLOR-LIGHT_TEXT_COLOR",
      "--COLOR-PRIMARY_BUTTON_1",
    ],
    borderColors: [
      "--COLOR-GAME_PASS_LEAGUE_ORDER_GRADIENT_1",
    ],
    titleColor: { cssVariable: "--COLOR-DARK_CONTAINER_BACKGROUND" },
    shortDescriptionColor: {
      cssVariable: "--COLOR-DARK_INACTIVE_BUTTON_TEXT",
    },
    longDescriptionColor: {
      cssVariable: "--COLOR-DARK_INACTIVE_BUTTON_TEXT",
    },
    detailsColor: { cssVariable: "--COLOR-PRIMARY_TEXT_COLOR" },
    timerBackgroundColor: {
      cssVariable: "--COLOR-LIGHT_TEXT_COLOR",
      opacity: 0.8,
    },
    timerTextColor: { cssVariable: "--COLOR-BETCARD_HEADER_BG" },
    timerIconColor: { cssVariable: "--COLOR-BETCARD_HEADER_BG" },
    timerBorderColor: { cssVariable: "--COLOR-BETCARD_HEADER_BG" },
    claimTextColor: { cssVariable: "--COLOR-LIGHT_TEXT_COLOR" },
    claimBackgroundColors: [
      "--COLOR-PRIMARY_BUTTON_0",
      "--COLOR-PRIMARY_BUTTON_1",
    ],
    claimBorderColor: { cssVariable: "--COLOR-PRIMARY" },
  },
  {
    theme: "dark",
    label: AppStrings.PAM_DYNAMIC_BONUS_BANNER_THEME_DARK,
    backgroundColors: [
      "--COLOR-PRIMARY_BUTTON_0",
      "--COLOR-PRIMARY_BUTTON_0",
    ],
    borderColors: ["--COLOR-PRIMARY"],
    titleColor: { cssVariable: "--COLOR-LIGHT_TEXT_COLOR" },
    shortDescriptionColor: { cssVariable: "--COLOR-NAVBAR_LABEL" },
    longDescriptionColor: { cssVariable: "--COLOR-NAVBAR_LABEL" },
    detailsColor: { cssVariable: "--COLOR-CHAT_MESSAGE_TEXT_COLOR" },
    timerBackgroundColor: {
      cssVariable: "--COLOR-LIGHT_TEXT_COLOR",
      opacity: 0.15,
    },
    timerTextColor: { cssVariable: "--COLOR-LIGHT_TEXT_COLOR" },
    timerIconColor: { cssVariable: "--COLOR-LIGHT_TEXT_COLOR" },
    timerBorderColor: { cssVariable: "--COLOR-LIGHT_TEXT_COLOR" },
    claimTextColor: { cssVariable: "--COLOR-PRIMARY_BACKGROUND_COLOR" },
    claimBackgroundColors: [
      "--COLOR-PRIMARY_BUTTON_0",
      "--COLOR-PRIMARY_BUTTON_1",
    ],
    claimBorderColor: { cssVariable: "--COLOR-PRIMARY" },
  },
];

export const getBannerThemeOptions = (): Array<{
  value: BannerTheme;
  label: string;
}> =>
  getBannerThemeConfigs().map(({ theme, label }) => ({
    value: theme,
    label,
  }));

const resolveColorSource = ({
  cssVariable,
  opacity,
}: ThemeColorSource): string => resolveThemeColor(cssVariable, opacity);

export const bannerThemePreset = (theme: BannerTheme): BannerThemePreset => {
  const config = getBannerThemeConfigs().find((item) => item.theme === theme);
  if (!config) {
    throw new Error(`Unknown banner theme: ${theme}`);
  }

  return {
    theme: config.theme,
    label: config.label,
    backgroundColors: resolveThemeColors(...config.backgroundColors),
    borderColors: resolveThemeColors(...config.borderColors),
    titleColor: resolveColorSource(config.titleColor),
    shortDescriptionColor: resolveColorSource(config.shortDescriptionColor),
    longDescriptionColor: resolveColorSource(config.longDescriptionColor),
    detailsColor: resolveColorSource(config.detailsColor),
    timerBackgroundColor: resolveColorSource(config.timerBackgroundColor),
    timerTextColor: resolveColorSource(config.timerTextColor),
    timerIconColor: resolveColorSource(config.timerIconColor),
    timerBorderColor: resolveColorSource(config.timerBorderColor),
    timerBorderWidth: 0,
    timerShadow: true,
    claimTextColor: resolveColorSource(config.claimTextColor),
    claimBackgroundColors: resolveThemeColors(
      ...config.claimBackgroundColors,
    ),
    claimBorderColor: resolveColorSource(config.claimBorderColor),
  };
};

export const applyBannerThemePreset = (
  banner: BannerIn | undefined,
  theme: BannerTheme,
): BannerIn => {
  const preset = bannerThemePreset(theme);

  return {
    ...banner,
    background_color: [...preset.backgroundColors],
    border_color: [...preset.borderColors],
    title: { ...banner?.title, color: preset.titleColor },
    short_description: {
      ...banner?.short_description,
      color: preset.shortDescriptionColor,
    },
    long_description: {
      ...banner?.long_description,
      color: preset.longDescriptionColor,
    },
    details: { ...banner?.details, color: preset.detailsColor },
    // Theme switch updates palette colors only. Preserve structural/effect
    // fields (border_width, shadow) for timer and claim — same as claim already
    // did via spread for those keys.
    timer: {
      ...banner?.timer,
      background_color: preset.timerBackgroundColor,
      text_color: preset.timerTextColor,
      icon_color: preset.timerIconColor,
      border_color: preset.timerBorderColor,
    },
    claim: {
      ...banner?.claim,
      text_color: preset.claimTextColor,
      background_color: [...preset.claimBackgroundColors],
      border_color: preset.claimBorderColor,
    },
  };
};

export const initializeBannerThemePreset = (
  banner: BannerIn | undefined,
  theme: BannerTheme,
): BannerIn => {
  const preset = bannerThemePreset(theme);

  return {
    ...banner,
    background_color: banner?.background_color?.length
      ? banner.background_color
      : [...preset.backgroundColors],
    border_color: banner?.border_color?.length
      ? banner.border_color
      : [...preset.borderColors],
    title: {
      ...banner?.title,
      color: banner?.title?.color ?? preset.titleColor,
    },
    short_description: {
      ...banner?.short_description,
      color:
        banner?.short_description?.color ?? preset.shortDescriptionColor,
    },
    long_description: {
      ...banner?.long_description,
      color: banner?.long_description?.color ?? preset.longDescriptionColor,
    },
    details: {
      ...banner?.details,
      visible: banner?.details?.visible ?? true,
      color: banner?.details?.color ?? preset.detailsColor,
    },
    timer: {
      ...banner?.timer,
      visible: banner?.timer?.visible ?? true,
      background_color:
        banner?.timer?.background_color ?? preset.timerBackgroundColor,
      text_color: banner?.timer?.text_color ?? preset.timerTextColor,
      icon_color: banner?.timer?.icon_color ?? preset.timerIconColor,
      border_color: banner?.timer?.border_color ?? preset.timerBorderColor,
      border_width: banner?.timer?.border_width ?? preset.timerBorderWidth,
      shadow: banner?.timer?.shadow ?? preset.timerShadow,
    },
    claim: {
      ...banner?.claim,
      visible: banner?.claim?.visible ?? false,
      text_color: banner?.claim?.text_color ?? preset.claimTextColor,
      background_color: banner?.claim?.background_color?.length
        ? banner.claim.background_color
        : [...preset.claimBackgroundColors],
      border_color: banner?.claim?.border_color ?? preset.claimBorderColor,
    },
  };
};
